import { Fragment } from 'react';

import SiteHeader from './SiteHeader';
import SiteFooter from './SiteFooter';
import type { LegalBlock, LegalDocumentContent, LegalListItem } from '../content/legal';

/**
 * Renders a legal document.
 *
 * ONE RENDERER FOR BOTH DOCUMENTS, so Terms and Privacy cannot drift apart
 * typographically and a change to legal readability happens once. The content lives in
 * content/legal.ts; this file decides only how it looks.
 *
 * The measure is deliberately narrower than the storefront's. A legal document is read,
 * not scanned, and a line length that works for a pricing table is punishing for eight
 * pages of prose.
 *
 * Emails and URLs that appear in the source text become links. That is presentation --
 * the address is already written in the document, and a legal notice that tells you to
 * email someone is more useful when you can.
 */

/** Matches an email address or an http(s) URL in running text. */
const LINKABLE = /([\w.+-]+@[\w-]+(?:\.[\w-]+)+|https?:\/\/[^\s<>()]+)/g;

/** Trailing sentence punctuation belongs to the sentence, not to the link. */
function splitTrailingPunctuation(token: string): [string, string] {
  const match = /[.,;:]+$/.exec(token);
  if (!match) return [token, ''];
  return [token.slice(0, match.index), token.slice(match.index)];
}

/**
 * Turn emails and URLs in a string into anchors, leaving every other character alone.
 *
 * Deliberately conservative: it adds anchors and changes nothing else. No punctuation is
 * moved, no whitespace collapsed, no word altered.
 */
function linkify(text: string, keyPrefix: string) {
  return text.split(LINKABLE).map((part, index) => {
    if (index % 2 === 0) return <Fragment key={`${keyPrefix}-t${index}`}>{part}</Fragment>;

    const [target, trailing] = splitTrailingPunctuation(part);
    const href = target.includes('@') ? `mailto:${target}` : target;

    return (
      <Fragment key={`${keyPrefix}-l${index}`}>
        <a href={href} className="mm-legal-link">
          {target}
        </a>
        {trailing}
      </Fragment>
    );
  });
}

function ListItemContent({ item, id }: { item: LegalListItem; id: string }) {
  if (typeof item === 'string') return <>{linkify(item, id)}</>;

  return (
    <>
      <strong className="font-semibold">{item.lead}</strong>
      {linkify(item.rest, id)}
    </>
  );
}

function Block({ block, id }: { block: LegalBlock; id: string }) {
  switch (block.kind) {
    case 'subheading':
      return <h3 className="mm-legal-subheading">{block.text}</h3>;

    case 'list':
      return (
        <ul className="mm-legal-list">
          {block.items.map((item, index) => (
            <li key={`${id}-i${index}`}>
              <ListItemContent item={item} id={`${id}-i${index}`} />
            </li>
          ))}
        </ul>
      );

    case 'callout':
      return <p className="mm-legal-callout">{block.text}</p>;

    case 'address':
      return (
        <address className="mm-legal-address">
          {block.lines.map((line, index) => (
            <span key={`${id}-a${index}`} className="block">
              {linkify(line, `${id}-a${index}`)}
            </span>
          ))}
        </address>
      );

    case 'paragraph':
    default:
      return <p className="mm-legal-paragraph">{linkify(block.text, id)}</p>;
  }
}

export default function LegalDocument({ document }: { document: LegalDocumentContent }) {
  return (
    <div className="flex min-h-dvh flex-col">
      <SiteHeader />

      <main className="mx-auto w-full max-w-[46rem] px-5 py-10 sm:px-8 sm:py-14 flex-1 min-w-0">
        <article data-testid="legal-document">
          <h1 className="mm-display">{document.title}</h1>

          <p className="mm-help mt-3" data-testid="legal-date">
            <strong className="font-semibold">{document.dateLabel}:</strong> {document.date}
          </p>

          <div className="mt-8">
            {document.intro.map((block, index) => (
              <Block key={`intro-${index}`} block={block} id={`intro-${index}`} />
            ))}
          </div>

          {document.sections.map((section, sectionIndex) => (
            <section key={section.heading} className="mt-10">
              <h2 className="mm-legal-heading">{section.heading}</h2>

              {section.blocks.map((block, blockIndex) => (
                <Block
                  key={`s${sectionIndex}-b${blockIndex}`}
                  block={block}
                  id={`s${sectionIndex}-b${blockIndex}`}
                />
              ))}
            </section>
          ))}
        </article>
      </main>

      <SiteFooter />
    </div>
  );
}
